import { createSlice } from "@reduxjs/toolkit";
import ApiUsersManagement from "../../api/user-manager";

export const usersManagementSlice = createSlice({
  name: "usersManagement",
  initialState: {
    users: [],
    hasMore: false,
    isOpenProfile: false,
    placement: "right",
    user: null,
    isLoading: false,
  },
  reducers: {
    // A user already listed is replaced by the fresher copy, not kept: the
    // profile reloads the list after a save, and the page puts the listed copy
    // back into the open profile — so a kept copy showed the save undone.
    appendUsers: (state, action) => {
      const newUsers = action.payload || [];
      const fresh = new Map(newUsers.map((u) => [u.id, u]));
      const existingIds = new Set(state.users.map(u => u.id));
      return {
        ...state,
        users: [
          ...state.users.map((u) => fresh.get(u.id) || u),
          ...newUsers.filter(u => !existingIds.has(u.id)),
        ],
        isLoading: false,
      };
    },
    setUsers: (state, action) => ({
      ...state,
      users: action.payload,
      user: state.user
        ? action.payload.filter((u) => u.id === state.user.id)[0]
        : null,
      hasMore: action.payload?.length && action.payload?.length === 5,
      isLoading: false,
    }),
    resetUsers: (state) => ({
      ...state,
      users: [],
      hasMore: false,
      isLoading: false,
    }),
    upsertUser: (state, action) => {
      const user = action.payload;
      if (!user?.id) return state;

      const users = state.users || [];
      const index = users.findIndex((item) => item.id === user.id);
      const nextUsers =
        index === -1
          ? users
          : users.map((item) =>
              item.id === user.id ? { ...item, ...user } : item
            );

      return {
        ...state,
        users: nextUsers,
        user:
          state.user?.id === user.id
            ? { ...state.user, ...user }
            : state.user,
      };
    },
    removeUsers: (state, action) => {
      const ids = new Set((action.payload || []).filter(Boolean));
      if (!ids.size) return state;

      return {
        ...state,
        users: (state.users || []).filter((user) => !ids.has(user.id)),
        user: state.user && ids.has(state.user.id) ? null : state.user,
      };
    },
    setIsOpenProfile: (state, action) => ({
      ...state,
      isOpenProfile: action.payload,
    }),
    setProfilePlacement: (state, action) => ({
      ...state,
      placement: action.payload,
    }),
    setProfileUser: (state, action) => ({
      ...state,
      user: action.payload,
    }),
    setLoading: (state) => ({ ...state, isLoading: true }),
  },
});

export const {
  setUsers,
  resetUsers,
  setProfileUser,
  setProfilePlacement,
  setIsOpenProfile,
  setLoading,
  appendUsers,
  upsertUser,
  removeUsers
} = usersManagementSlice.actions;

export const fetchUsers = (query) => (dispatch) => {
  dispatch(setLoading());
  ApiUsersManagement.getUsers(query).then((r) => {
    // dispatch(setUsers(r.data.users));
    dispatch(appendUsers(r.data.users));
  });
};

export const clearUsers = () => (dispatch) => {
  dispatch(resetUsers());
};

export const selectUsers = (state) => state.usersManagement.users;
export const selectHasMoreUsers = (state) => state.usersManagement.hasMore;
export const selectProfilePlacement = (state) =>
  state.usersManagement.placement;
export const selectIsOpenProfile = (state) =>
  state.usersManagement.isOpenProfile;
export const selectProfileUser = (state) => state.usersManagement.user;
export const selectUsersLoading = (state) => state.usersManagement.isLoading;

export default usersManagementSlice.reducer;
